/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ApiError, BulkArchiveResponse } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type BulkArchiveNodesRequest = {
  nodeIds: string[];
};

export type BulkArchiveNodesMutationConfig = {
  onSuccess?: (data: BulkArchiveResponse, variables: BulkArchiveNodesRequest) => void;
  onError?: (error: ApiError, variables: BulkArchiveNodesRequest) => void;
  onSettled?: (
    data: BulkArchiveResponse | undefined,
    error: ApiError | null,
    variables: BulkArchiveNodesRequest,
  ) => void;
  onMutate?: (variables: BulkArchiveNodesRequest) => void;
};

export function useBulkArchiveNodesMutation({
  onSuccess,
  onError,
  onSettled,
  onMutate,
}: BulkArchiveNodesMutationConfig) {
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const queryClient = useQueryClient();

  const { mutateAsync, isPending, isError, error } = useMutation<
    BulkArchiveResponse,
    ApiError,
    BulkArchiveNodesRequest
  >({
    mutationFn: ({ nodeIds }) => {
      return mgmtPlaneAPIClient.nodes.bulkArchiveNodes({
        tenantId: tenantId!,
        requestBody: {
          nodes: nodeIds.map((nodeId) => ({ nodeId })),
        },
      });
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['nodes'] });

      variables.nodeIds.forEach((nodeId) => {
        queryClient.invalidateQueries({ queryKey: ['node', tenantId, nodeId] });
      });

      onSuccess?.(data, variables);
    },
    onError,
    onSettled,
    onMutate,
  });

  return { mutateAsync, isPending, isError, error };
}
